// EmployeeRecordModal — read-only "view all records for one employee",
// opened by clicking an employee's name on Operations → Roster. Aggregates
// every per-person compliance record: DISA / background and Training / certs
// today, with a placeholder for ISN (which is CLIENT-level in this app, not
// per-employee — see Operations → ISNetworld).
//
// Data: GET /api/workforce/employees/:id/records -> { employee, disa, training }.
// Each record type is its own <Section>, so adding a new axis later is just one
// more Section here + one more key on the aggregator payload.
//
// The expiry helpers are duplicated from pages/Operations.jsx (module-scope
// there, not exported). Keep the ≤30-day red / ≤60-day orange thresholds in
// sync with Operations.jsx expiryColor + the server disaRedReport/compliance
// alert emails.

import { useEffect, useState } from 'react';
import { colors, styles, fmtDate, fmtDateTime } from './UI';
import { api } from '../api';
// CrewCompliance imports this file back for its "Employee record" link; both
// sides use the other only at render time (see the note there).
import { ComplianceChip, ComplianceModal } from './CrewCompliance';
import { openItems } from '../utils/siteAccessView';

// "Cleared for" order: where he can go first (ready, then expiring), then
// not ready, then customers with no rules to judge.
const CLEARED_RANK = { green: 0, amber: 1, red: 2, gray: 3 };

// Fleet-standard modal chrome (re-declared per file; BadgingFiller/EventModal
// do the same — there is no shared Modal component).
const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '40px 16px', zIndex: 1000, overflowY: 'auto',
};
const card = {
  background: '#fff', borderRadius: 8, padding: 24,
  maxWidth: 720, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};
const h2 = { margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#1F2937' };
const sub = { fontSize: 11, color: '#6B7280', marginBottom: 6 };
const fieldLabel = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7280', fontWeight: 700, marginBottom: 2 };

const daysUntil = (dateStr) => { const d = new Date(dateStr); const now = new Date(); return Math.floor((d - now) / 86400000); };
const expiryColor = (days) => {
  if (days === null) return colors.textMuted;
  if (days < 0) return colors.red;
  if (days <= 30) return colors.red;
  if (days <= 60) return colors.orange;
  return colors.green;
};
const expiryChip = (dateStr) => {
  if (!dateStr) return <span style={{ color: colors.textMuted, fontSize: 11 }}>—</span>;
  const days = daysUntil(dateStr);
  const color = expiryColor(days);
  const label = days < 0 ? `Expired ${-days}d` : days === 0 ? 'Expires today' : days <= 60 ? `${days}d left` : `${days}d`;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', fontSize: 11 }}>
      <span style={{ fontWeight: 600 }}>{fmtDate(dateStr)}</span>
      <span style={{ color, fontSize: 10 }}>{label}</span>
    </span>
  );
};

// One record axis. Add a new <Section> to extend the record view.
function Section({ icon, title, count, children }) {
  return (
    <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 14, marginTop: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', marginBottom: 8 }}>
        {icon} {title}{count != null ? ` (${count})` : ''}
      </div>
      {children}
    </div>
  );
}

export function EmployeeRecordModal({ employee, onClose, onError }) {
  const [data, setData] = useState(null);
  // Person-first Site Access (GET /employees/:id/site-access, 2026-09-23):
  // this employee against every customer / site requirements row.
  const [access, setAccess] = useState(null);
  const [accessError, setAccessError] = useState('');
  const [accessEntry, setAccessEntry] = useState(null); // row opened in the compliance modal
  const [accessTick, setAccessTick] = useState(0); // bumped after a badge change re-judges him

  useEffect(() => {
    let live = true;
    setData(null);
    api.employeeRecords(employee.id)
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live && onError) onError(e.message); });
    return () => { live = false; };
  }, [employee.id]);

  useEffect(() => { setAccess(null); setAccessEntry(null); }, [employee.id]);
  useEffect(() => {
    let live = true;
    setAccessError('');
    api.employeeSiteAccess(employee.id)
      .then(d => {
        if (!live) return;
        setAccess(d);
        // Keep an open compliance modal on the re-judged row.
        setAccessEntry(cur => {
          if (!cur) return cur;
          const r = (d.customers || []).find(x => x.customer_id === cur.row.customer_id
            && (x.site_id || null) === (cur.row.site_id || null));
          return r ? { entry: { ...cur.entry, status: r.status, disa_status: r.disa_status, checks: r.checks || [], badging_docs: r.badging_docs || [] }, row: r } : null;
        });
      })
      .catch(e => { if (live) setAccessError(e.message); });
    return () => { live = false; };
  }, [employee.id, accessTick]);

  const clearedRows = [...((access && access.customers) || [])].sort((a, b) =>
    (CLEARED_RANK[a.status] ?? 3) - (CLEARED_RANK[b.status] ?? 3)
    || String(a.customer_name || '').localeCompare(String(b.customer_name || ''))
    || String(a.site_name || '').localeCompare(String(b.site_name || '')));

  const disa = data?.disa;
  const training = data?.training || [];
  const hasDisa = disa && (disa.disa_id || disa.drug_test_date || disa.background_check_date ||
    disa.background_check_expiry || (Array.isArray(disa.policies) && disa.policies.length));

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={card}>
        <h2 style={h2}>🗂 Employee Record — {employee.full_name}</h2>
        <div style={sub}>
          {[employee.craft, employee.employee_no && `#${employee.employee_no}`, employee.type]
            .filter(Boolean).join(' · ') || 'Roster employee'}
        </div>
        {access && access.possible_duplicate_of && access.possible_duplicate_of.name && (
          <div style={{ fontSize: 12, marginBottom: 6 }}>
            <span style={{ background: '#EDE9FE', color: '#5B21B6', borderRadius: 10, padding: '2px 8px', fontWeight: 700 }}
              title="Same surname and a first-name variant of another active roster row, and this row has no DISA or training record of its own. Check the other row first. Nothing is ever merged automatically.">
              Possible CRM duplicate of {access.possible_duplicate_of.name}
            </span>
          </div>
        )}

        {!data ? (
          <div style={{ color: '#6B7280', fontSize: 13, padding: '14px 0' }}>Loading…</div>
        ) : (
          <>
            {/* DISA / Background */}
            <Section icon="🧪" title="DISA / Background">
              {!hasDisa
                ? <div style={{ color: '#9CA3AF', fontSize: 12 }}>No DISA record on file yet.</div>
                : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={fieldLabel}>Active Policies</div>
                      {Array.isArray(disa.policies) && disa.policies.length
                        ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {disa.policies.map(p => (
                              <span key={p} title={p} style={{ background: '#E0E7FF', color: '#3730A3', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{p}</span>
                            ))}
                          </div>
                        : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                    </div>
                    <div>
                      <div style={fieldLabel}>DISA Number</div>
                      {disa.disa_id ? <span style={{ fontSize: 13, fontFamily: 'monospace' }}>{disa.disa_id}</span> : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                    </div>
                    <div>
                      <div style={fieldLabel}>SSN (Last 4)</div>
                      {disa.ssn_last4 ? <span style={{ fontSize: 13, fontFamily: 'monospace' }}>XXX-XX-{disa.ssn_last4}</span> : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                    </div>
                    <div>
                      <div style={fieldLabel}>Last Drug Test</div>
                      {disa.drug_test_date
                        ? <div style={{ fontSize: 13 }}>{fmtDate(disa.drug_test_date)}{disa.last_test_purpose && <span style={{ color: '#6B7280', fontStyle: 'italic', fontSize: 11 }}> · {disa.last_test_purpose}</span>}</div>
                        : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                    </div>
                    <div><div style={fieldLabel}>Drug Test Expiry</div>{expiryChip(disa.drug_test_expiry)}</div>
                    <div>
                      <div style={fieldLabel}>Background Check</div>
                      {disa.background_check_date ? <span style={{ fontSize: 13 }}>{fmtDate(disa.background_check_date)}</span> : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                    </div>
                    <div><div style={fieldLabel}>Background Expiry</div>{expiryChip(disa.background_check_expiry)}</div>
                    <div><div style={fieldLabel}>Random Pool</div><span style={{ fontSize: 13 }}>{disa.random_pool ? '✓ In pool' : '—'}</span></div>
                    <div>
                      <div style={fieldLabel}>Source</div>
                      {disa.source === 'disa_import'
                        ? <span style={{ color: '#065F46', fontWeight: 600, fontSize: 12 }} title={disa.imported_at ? `Imported ${fmtDateTime(disa.imported_at)}` : ''}>📥 DISA sync{disa.imported_at ? ` · ${fmtDate(disa.imported_at)}` : ''}</span>
                        : disa.updated_at ? <span style={{ fontSize: 12 }} title={disa.updated_by_name ? `by ${disa.updated_by_name}` : ''}>Manual · {fmtDate(disa.updated_at)}</span> : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </div>
                    {disa.notes && <div style={{ gridColumn: '1 / -1' }}><div style={fieldLabel}>Notes</div><span style={{ fontSize: 12 }}>{disa.notes}</span></div>}
                  </div>
                )}
            </Section>

            {/* Training / Certifications */}
            <Section icon="🎓" title="Training / Certifications" count={training.length}>
              {!training.length
                ? <div style={{ color: '#9CA3AF', fontSize: 12 }}>No certifications recorded.</div>
                : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ ...styles.table, fontSize: 12 }}>
                      <thead><tr>
                        <th style={styles.th}>Certification</th>
                        <th style={styles.th}>Issued</th>
                        <th style={styles.th}>Expires</th>
                        <th style={styles.th}>Cert #</th>
                        <th style={styles.th}>Issuer</th>
                      </tr></thead>
                      <tbody>
                        {training.map(t => (
                          <tr key={t.id}>
                            <td style={{ ...styles.td, fontWeight: 600 }}>{t.cert_type}</td>
                            <td style={styles.td}>{t.issued_date ? fmtDate(t.issued_date) : '—'}</td>
                            <td style={styles.td}>{expiryChip(t.expiry_date)}</td>
                            <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{t.certificate_no || '—'}</td>
                            <td style={styles.td}>{t.issuer || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </Section>

            {/* ISNetworld — per-employee ISN number captured from the DISA report
                (stopgap until a dedicated ISN upload exists). */}
            <Section icon="🌐" title="ISNetworld">
              {disa && disa.isn_number ? (
                <div>
                  <div style={fieldLabel}>ISN Number</div>
                  <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: '#1F2937' }}>{disa.isn_number}</span>
                  <span style={{ color: '#6B7280', fontSize: 11, marginLeft: 8 }}>from DISA report</span>
                </div>
              ) : (
                <div style={{ color: '#9CA3AF', fontSize: 12 }}>
                  No ISN number on the DISA report for this employee.{' '}
                  <span style={{ fontSize: 11 }}>(Company-level ISN standing is on Operations → ISNetworld.)</span>
                </div>
              )}
            </Section>
          </>
        )}

        {/* Cleared for: this one person against every customer and site
            requirements row (GET /workforce/employees/:id/site-access), as of
            today. Same engine and chip as Site Access. */}
        <Section icon="🔑" title="Cleared for" count={access ? clearedRows.length : null}>
          {accessError ? (
            <div style={{ color: '#B91C1C', fontSize: 12 }}>Could not load site access: {accessError}</div>
          ) : !access ? (
            <div style={{ color: '#6B7280', fontSize: 12 }}>Loading…</div>
          ) : !clearedRows.length ? (
            <div style={{ color: '#9CA3AF', fontSize: 12 }}>No customer has site requirements or an ISNetworld link to judge him against.</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>
                <strong style={{ color: '#065F46' }}>{access.counts.green || 0} ready</strong>
                {' · '}<strong style={{ color: '#92400E' }}>{access.counts.amber || 0} expiring</strong>
                {' · '}<strong style={{ color: '#991B1B' }}>{access.counts.red || 0} not ready</strong>
                {' · '}<span style={{ color: '#6B7280' }}>{access.counts.gray || 0} no rules</span>
                <span style={{ color: '#9CA3AF', fontSize: 11 }}> (as of today; click a chip for every check)</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ ...styles.table, fontSize: 12 }}>
                  <thead><tr>
                    <th style={styles.th}>Customer</th>
                    <th style={styles.th}>Requirements</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Missing</th>
                  </tr></thead>
                  <tbody>
                    {clearedRows.map((r, i) => {
                      const entry = {
                        employee_id: access.employee.id, name: access.employee.name,
                        employee_no: access.employee.employee_no, craft: access.employee.craft,
                        inactive: !access.employee.is_active,
                        status: r.status, disa_status: r.disa_status, checks: r.checks || [],
                        badging_docs: r.badging_docs || [], compliance_through: access.compliance_through,
                        possible_duplicate_of: access.possible_duplicate_of,
                      };
                      const open = openItems(entry);
                      return (
                        <tr key={`${r.customer_id}-${r.site_id || 0}-${i}`}>
                          <td style={{ ...styles.td, fontWeight: 600 }}>{r.customer_name}{r.site_name ? <span style={{ color: '#6B7280', fontWeight: 400 }}> · {r.site_name}</span> : ''}</td>
                          <td style={{ ...styles.td, color: '#6B7280' }}>{r.requirements_source || '—'}</td>
                          <td style={styles.td}>
                            <ComplianceChip entry={entry} onClick={() => setAccessEntry({ entry, row: r })} />
                          </td>
                          <td style={{ ...styles.td, fontSize: 11 }}>
                            {open.length ? open.map(c => c.label).join(', ') : <span style={{ color: '#9CA3AF' }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {access && access.isn_unassigned && access.isn_unassigned.length > 0 && (
            <div style={{ fontSize: 11, color: '#B45309', marginTop: 6 }}>
              Linked in ISNetworld but nothing assigned to him there (and no 📋 Reqs row): {access.isn_unassigned.map(c => c.customer_name).join(', ')}.
            </div>
          )}
        </Section>

        {accessEntry && (
          <ComplianceModal
            entry={accessEntry.entry}
            job={{
              customer: { name: accessEntry.row.customer_name + (accessEntry.row.site_name ? ` · ${accessEntry.row.site_name}` : '') },
              requirements_source: accessEntry.row.requirements_source,
              isn: accessEntry.row.isn || null,
            }}
            hideRecordLink
            onChanged={() => setAccessTick(t => t + 1)}
            onClose={() => setAccessEntry(null)}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button style={styles.btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
